// Automatisch gegenereerd door tools/generate-dto.mjs — niet handmatig wijzigen.
// Bij herGeneratie worden imports, constructor-body en static create bewaard.

import { ILevelDomainDto } from 'src/app/infrastructure/models/level-domain-dto';
import { IActivityData, activityData } from 'src/app/features/activity/models/activity-data';
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';
import { IClaimListDto } from './claim-list-dto.g';
import { claimDto, ClaimDto, IClaimDto } from './claim-dto.g';
import { IRoleDto, roleDto, RoleDto } from './role-dto.g';
import { IClaimRefDto } from './claim-ref-dto.g';
import { IRoleRefDto } from './role-ref-dto.g';
import { IPageListDto, pageListDto, PageListDto } from './page-list-dto.g';

export const activityDto = {
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    deleted: 'deleted',
    inactive: 'inactive',
    claimsDto: 'claimsDto',
    rolesDto: 'rolesDto',
    pagesDto: 'pagesDto',
} as const;

export type TActivityDto = (typeof activityDto)[keyof typeof activityDto];

export const activityDtoConstraints = {
    name: { required: true },
    guid: { required: true },
} as const;

export type TActivityDtoConstraints = typeof activityDtoConstraints;

export interface IActivityDto extends ILevelDomainDto {
    [activityDto.guid]: string | null;
    [activityDto.nr]: number | null;
    [activityDto.name]: string | null;
    [activityDto.deleted]: boolean | null;
    [activityDto.inactive]: boolean | null;
    [activityDto.claimsDto]: IClaimDto[] | null;
    [activityDto.rolesDto]: IRoleRefDto[] | null;
    [activityDto.pagesDto]: IPageListDto[] | null;
}

export class ActivityDto
    implements IActivityDto
{
    [activityDto.guid]: string | null = null;
    [activityDto.nr]: number | null = null;
    [activityDto.name]: string | null = null;
    [activityDto.deleted]: boolean | null = null;
    [activityDto.inactive]: boolean | null = null;
    [activityDto.claimsDto]: IClaimDto[] | null = null;
    [activityDto.rolesDto]: IRoleRefDto[] | null = null;
    [activityDto.pagesDto]: IPageListDto[] | null = null;

    private constructor(
        modelData?: IActivityData,
        includeRefs = true,
    ) {
        if (!modelData || isEmptyObject(modelData)) return;

        this[activityDto.guid] = modelData?.[activityData.guid];
        this[activityDto.nr] = modelData?.[activityData.nr];
        this[activityDto.name] = modelData?.[activityData.name];
        this[activityDto.deleted] = modelData?.[activityData.deleted];
        this[activityDto.inactive] = modelData?.[activityData.inactive];

        if (!includeRefs) return;

        this[activityDto.claimsDto] = modelData[activityData.claimsData]
            ?.map(item => ClaimDto.create(item))
            .filter((a): a is IClaimDto => a !== null)
            .sort((a, b) => (a[claimDto.name] ?? '').localeCompare(b[claimDto.name] ?? ''))
            ?? null;
        this[activityDto.rolesDto] = modelData[activityData.rolesData]
            ?.map(item => RoleDto.create(item))
            .filter((a): a is IRoleDto => a !== null)
            .sort((a, b) => (a[roleDto.name] ?? '').localeCompare(b[roleDto.name] ?? ''))
            ?? null;
        this[activityDto.pagesDto] = modelData[activityData.pagesData]
            ?.map(item => PageListDto.create(item))
            .filter((a): a is IPageListDto => a !== null)
            .sort((a, b) => (a[pageListDto.name] ?? '').localeCompare(b[pageListDto.name] ?? ''))
            ?? null;
    }

    static create(modelData?: IActivityData | null, includeRefs = true): IActivityDto | null {
        return modelData ? new ActivityDto(modelData, includeRefs) : null;
    }
}
